import React, { FC, memo, ReactNode } from 'react';
import { useMutation } from 'react-query';
import { BusinessPartnersAPI } from 'api/BusinessPartnersAPI';
import { MedicalOperator } from 'models/medicalOperator';
import {
  ExclamationCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons/lib';
import { Button, message, Modal, Tooltip } from 'antd';
import { ButtonProps } from 'antd/lib/button';
import { DeleteUserStatus } from 'models/deleteUserStatus';
import { useFormatMessage } from 'common/hooks/useFormatMessage';

export interface BusinessPartnerDeleteMedicalOperatorButtonProps
  extends Omit<ButtonProps, 'onClick' | 'loading' | 'href'> {
  businessPartnerId: number;
  medicalOperator: MedicalOperator;
  status?: DeleteUserStatus;
  tooltip?: ReactNode;
  onDeleted?: () => void;
}

export const DeleteMedicalOperatorButton: FC<BusinessPartnerDeleteMedicalOperatorButtonProps> = memo(
  ({
    businessPartnerId,
    medicalOperator,
    status = DeleteUserStatus.Delete,
    onDeleted,
    tooltip = 'Archive medicalOperator',
    ...buttonProps
  }) => {
    const [mutate, { isLoading }] = useMutation(
      BusinessPartnersAPI.deleteMedicalOperator
    );
    const intlMessage = useFormatMessage();

    const confirm = () =>
      Modal.confirm({
        title:
          DeleteUserStatus.Delete === status
            ? intlMessage(
                { id: 'entity.delete.confirmation' },
                {
                  name: `${medicalOperator.firstName} ${medicalOperator.lastName}`,
                }
              )
            : intlMessage(
                { id: 'entity.archive.confirmation' },
                {
                  name: `${medicalOperator.firstName} ${medicalOperator.lastName}`,
                }
              ),
        icon: <ExclamationCircleOutlined />,
        okText: intlMessage({ id: 'yes' }),
        cancelText: intlMessage({ id: 'cancel' }),
        okType: 'danger',
        onOk: () =>
          mutate(
            {
              businessPartnerId,
              dto: { userId: medicalOperator.userId, status },
            },
            {
              onSuccess: () => {
                message.success(
                  DeleteUserStatus.Delete === status
                    ? intlMessage(
                        { id: 'entity.delete.success' },
                        {
                          name: `${medicalOperator.firstName} ${medicalOperator.lastName}`,
                        }
                      )
                    : intlMessage(
                        { id: 'entity.archive.success' },
                        {
                          name: `${medicalOperator.firstName} ${medicalOperator.lastName}`,
                        }
                      )
                );
                onDeleted && onDeleted();
              },
            }
          ),
      });

    const button = (
      <Button
        id="button-delete-medical-operator"
        loading={isLoading}
        onClick={() => confirm()}
        icon={<WarningOutlined />}
        {...buttonProps}
      />
    );

    return tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button;
  }
);
